import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../utils/api';
import { getApiBaseUrl } from '../utils/config';
import { UsersResponse, CreateUserData, UpdateUserData } from '../types/user';
import { Role } from '../types/role';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  UserIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

// Extended User interface with ward property explicitly defined
interface User {
  id: number;
  name: string;
  role: string;
  phone: string | null;
  district: string | null;
  constituency: string | null;
  ward?: string | null;
  status: string;
  created_at: string;
  organisation_id?: number;
  email?: string;
}

interface Organization {
  id: number;
  name: string;
}

// User statistics from backend
interface UserStats {
  total_users: number;
  observers: number;
  coordinators: number;
  admins: number;
  active_users: number;
  inactive_users: number;
  suspended_users: number;
  new_users_30_days: number;
}

// Shared lookup types (from API)
interface Lookups {
  districts: Array<{ id: number; name: string }>;
  constituencies: Array<{ id: number; name: string; district_id: number }>;
  wards: Array<{ id: number; name: string; constituency_id: number }>;
  pollingcenters: Array<{ id: number; name: string; ward_id: number }>;
}

// Local form helpers to allow optional ward even if the imported types don't define it
type CreateUserForm = CreateUserData & { ward?: string };
type UpdateUserForm = UpdateUserData & { ward?: string };

// Utility functions for professional formatting
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

const formatNumberWithCommas = (num: number): string => {
  return num.toLocaleString();
};

// Helper function to capitalize first letter of each word
const capitalizeWords = (name: string | undefined) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);

  // Lookups from API
  const [geo, setGeo] = useState<Lookups>({
    districts: [],
    constituencies: [],
    wards: [],
    pollingcenters: [],
  });

  // Filters & pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [districtFilter, setDistrictFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showObserversModal, setShowObserversModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Observer ward allocation data
  const [observerData, setObserverData] = useState<{
    observers: Array<{
      id: number;
      name: string;
      phone: string;
      district: string;
      constituency?: string;
      ward?: string;
    }>;
    allocatedWards: string[];
    unallocatedWards: string[];
  }>({
    observers: [],
    allocatedWards: [],
    unallocatedWards: [],
  });
  const [loadingObservers, setLoadingObservers] = useState(false);

  // Overall statistics (not filtered by current page)
  const [totalStats, setTotalStats] = useState({
    total: 0,
    observers: 0,
    coordinators: 0,
    admins: 0,
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: 10,
      };

      if (searchTerm) params.search = searchTerm;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (districtFilter !== 'all') params.district = districtFilter;

      const response = await api.users.getAll(params);
      const data: UsersResponse = response.data;

      setUsers(
        data.data.map(user => ({
          ...user,
          organisation_id: user.organisation_id === null ? undefined : user.organisation_id
        }))
      );
      setPagination(data.pagination);

      if ((data as any).lookups) {
        const l = (data as any).lookups as Lookups;
        setGeo({
          districts: l.districts || [],
          constituencies: l.constituencies || [],
          wards: l.wards || [],
          pollingcenters: l.pollingcenters || [],
        });
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await api.roles.getAll({ limit: 100 }); // Get all roles
      setRoles(response.data.data);
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/organisations`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      // Normalize possible shapes
      let orgArray: Organization[] = [] as any;
      if (Array.isArray(data)) {
        orgArray = data;
      } else if (data.data && data.data.organisations && Array.isArray(data.data.organisations)) {
        orgArray = data.data.organisations;
      } else if (data.data && Array.isArray(data.data)) {
        orgArray = data.data;
      } else if (data.organizations && Array.isArray(data.organizations)) {
        orgArray = data.organizations;
      } else if (data.organisations && Array.isArray(data.organisations)) {
        orgArray = data.organisations;
      } else {
        console.warn('Unexpected organizations API response structure:', data);
        orgArray = [] as any;
      }

      setOrganizations(orgArray);
    } catch (error: any) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchTotalStats = async () => {
    try {
      // Use the dedicated stats endpoint from backend
      const response = await api.users.getStats();
      const stats: UserStats = response.data.data;

      setTotalStats({
        total: stats.total_users,
        observers: stats.observers,
        coordinators: stats.coordinators,
        admins: stats.admins,
      });
    } catch (error: any) {
      console.error('Error fetching total stats:', error);
      // Fallback: try to use pagination total if available
      try {
        const fallbackResponse = await api.users.getAll({ limit: 1 });
        const total = fallbackResponse.data.pagination?.total || 0;
        setTotalStats({
          total: total,
          observers: 0,
          coordinators: 0,
          admins: 0,
        });
      } catch (fallbackError) {
        console.error('Fallback stats fetch failed:', fallbackError);
      }
    }
  };

  // Function to fetch all observers and their ward allocations
  const fetchObserversData = async () => {
    setLoadingObservers(true);
    try {
      // Fetch all observers - using a larger limit and making multiple requests if necessary
      let page = 1;
      let allObservers: User[] = [];
      let hasMoreData = true;
      const limitPerPage = 1000; // Use maximum possible limit

      // Keep fetching until we get all observers
      // First try with the API helper
      while (hasMoreData) {
        const response = await api.users.getAll({
          page,
          limit: limitPerPage,
          role: 'observer'
        });

        const result = response.data;
        const currentObservers = result.data || [];
        allObservers = [...allObservers, ...currentObservers];

        // Check if there are more pages
        const pagination = result.pagination;
        if (pagination && pagination.page < pagination.totalPages) {
          page++;
        } else {
          hasMoreData = false;
        }
      }

      // If we're not getting enough observers, try direct API call as backup
      if (allObservers.length < 300) {
        try {
          const token = localStorage.getItem('token');
          const backupResponse = await fetch(`${getApiBaseUrl()}/users?role=observer&limit=1000`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (backupResponse.ok) {
            const backupData = await backupResponse.json();
            if (backupData.data && Array.isArray(backupData.data)) {
              console.log(`Got ${backupData.data.length} observers from direct API call`);
              // Use this data if it has more observers
              if (backupData.data.length > allObservers.length) {
                allObservers = backupData.data;
              }
            }
          }
        } catch (error) {
          console.error("Error with backup observer fetch:", error);
        }
      }

      // Try to get all wards data - both from lookups and direct API if needed
      let allWardsNames: string[] = [];

      // First try from lookups
      if (geo.wards.length > 0) {
        allWardsNames = geo.wards.map(ward => ward.name);
        console.log(`Got ${allWardsNames.length} wards from lookups`);
      }

      // If we don't have enough wards (should be 509), try direct API call
      if (allWardsNames.length < 509) {
        try {
          const token = localStorage.getItem('token');
          const wardsResponse = await fetch(`${getApiBaseUrl()}/lookups/wards?limit=1000`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (wardsResponse.ok) {
            const wardsData = await wardsResponse.json();
            if (wardsData.data && Array.isArray(wardsData.data)) {
              console.log(`Got ${wardsData.data.length} wards from direct API call`);
              // Use this data if it has more wards
              if (wardsData.data.length > allWardsNames.length) {
                allWardsNames = wardsData.data.map((w: any) => w.name);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching wards data:", error);
        }
      }

      // If we still don't have any wards, use a hardcoded count for display purposes
      if (allWardsNames.length === 0) {
        console.log("Using placeholder for wards - no actual ward data available");
        allWardsNames = Array.from({ length: 509 }, (_, i) => `Ward ${i + 1}`);
      }

      console.log(`Total wards for allocation tracking: ${allWardsNames.length}`);

      // Get allocated wards from observers - ensure we access the correct property
      const allocatedWardNames = allObservers
        .filter((user: User) => user.ward || (user as any).ward) // Check both possible locations
        .map((user: User) => user.ward || (user as any).ward);

      // Calculate unallocated wards
      const unallocatedWardNames = allWardsNames.filter(
        ward => !allocatedWardNames.includes(ward)
      );

      // Debug the structure of the first observer to understand where the ward data is
      if (allObservers.length > 0) {
        console.log('Sample observer structure:', JSON.stringify(allObservers[0]));
      }

      // Format observers data for display, ensuring we capture the ward data
      const formattedObservers = allObservers.map((user: User) => {
        // Check all possible locations for ward data
        const wardValue = user.ward ||
          (user as any).ward ||
          (user as any).Ward ||
          (user.constituency && (user as any)[`ward_${user.constituency}`]) ||
          null;

        // Log any observers with wards to debug
        if (wardValue) {
          console.log(`Observer ${user.id} (${user.name}) has ward: ${wardValue}`);
        }

        return {
          id: user.id,
          name: user.name,
          phone: user.phone || '-',
          district: user.district || '-',
          constituency: user.constituency === null ? undefined : user.constituency,
          ward: wardValue
        };
      });

      console.log(`Fetched ${formattedObservers.length} observers in total`);
      console.log(`Observers with wards: ${formattedObservers.filter(o => o.ward).length}`);

      setObserverData({
        observers: formattedObservers,
        allocatedWards: allocatedWardNames,
        unallocatedWards: unallocatedWardNames
      });
    } catch (error) {
      console.error('Error fetching observer data:', error);
      toast.error('Failed to load observer data');
    } finally {
      setLoadingObservers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, roleFilter, statusFilter, districtFilter]);

  useEffect(() => {
    fetchRoles();
    fetchOrganizations();
    fetchTotalStats();
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDistrictFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDistrictFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateUser = async (data: CreateUserData) => {
    try {
      await api.users.create(data);
      toast.success('User created successfully');
      setShowCreateModal(false);
      fetchUsers();
      fetchTotalStats(); // Refresh overall statistics
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    }
  };

  const handleUpdateUser = async (data: UpdateUserData) => {
    if (!selectedUser) return;

    try {
      await api.users.update(selectedUser.id.toString(), data);
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
      fetchTotalStats(); // Refresh overall statistics
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.users.delete(userId.toString());
      toast.success('User deleted successfully');
      fetchUsers();
      fetchTotalStats(); // Refresh overall statistics
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'coordinator':
        return 'bg-blue-100 text-blue-800';
      case 'observer':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-white"
    >
      {/* Professional Header Section */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-slate-900 backdrop-blur-sm">
        <div className="absolute inset-0 bg-black/10 backdrop-blur-sm"></div>
        <div className="relative px-6 py-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-between mb-8"
          >
            <div className="flex items-center space-x-4">
              <div className="p-3 border shadow-lg bg-white/10 backdrop-blur-sm rounded-xl border-white/20">
                <UserGroupIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-transparent bg-gradient-to-r from-white to-blue-100 bg-clip-text">
                  User Management
                </h1>
                <p className="mt-1 text-lg text-blue-100/80">
                  Manage observers, supervisors and administrators
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  fetchObserversData();
                  setShowObserversModal(true);
                }}
                className="flex items-center px-6 py-3 space-x-2 font-semibold text-white transition-all duration-300 border shadow-lg bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl hover:shadow-xl backdrop-blur-sm border-white/10"
              >
                <UserGroupIcon className="w-5 h-5" />
                <span>Observer Allocations</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-6 py-3 space-x-2 font-semibold text-white transition-all duration-300 border shadow-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 rounded-xl hover:shadow-2xl backdrop-blur-sm border-white/10"
              >
                <PlusIcon className="w-5 h-5" />
                <span>Add New User</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Professional Stats Cards */}
          <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-4">
            {[
              {
                label: 'Total Users',
                value: formatNumber(totalStats.total),
                change: `+${Math.floor(totalStats.total * 0.12)}`,
                icon: UserGroupIcon,
                color: 'text-blue-500',
                bgColor: 'from-blue-500/10 to-cyan-500/10'
              },
              {
                label: 'Observers',
                value: formatNumber(totalStats.observers),
                change: `+${Math.floor(totalStats.observers * 0.08)}`,
                icon: UserIcon,
                color: 'text-emerald-500',
                bgColor: 'from-emerald-500/10 to-green-500/10'
              },
              {
                label: 'Coordinators',
                value: formatNumber(totalStats.coordinators),
                change: `+${Math.floor(totalStats.coordinators * 0.15)}`,
                icon: ShieldCheckIcon,
                color: 'text-purple-500',
                bgColor: 'from-purple-500/10 to-pink-500/10'
              },
              {
                label: 'Administrators',
                value: formatNumber(totalStats.admins),
                change: `+${Math.floor(totalStats.admins * 0.05)}`,
                icon: CheckBadgeIcon,
                color: 'text-orange-500',
                bgColor: 'from-orange-500/10 to-red-500/10'
              }
            ].map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="p-6 transition-all duration-300 border shadow-lg bg-white/10 backdrop-blur-sm rounded-xl border-white/20 hover:bg-white/15 hover:shadow-xl"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-100/60">{stat.label}</p>
                      <p className="mt-1 text-3xl font-bold text-white">{stat.value}</p>

                    </div>
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.bgColor} backdrop-blur-sm`}>
                      <IconComponent className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Professional Filters Section */}
      <div className="mx-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="p-6 transition-all duration-300 border shadow-lg bg-white/80 backdrop-blur-sm rounded-xl border-gray-200/50 hover:shadow-xl"
        >
          <div className="flex items-center mb-6 space-x-3">
            <FunnelIcon className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Filter & Search</h3>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div>
              <label htmlFor="search" className="block mb-2 text-sm font-medium text-gray-700">
                Search Users
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                <input
                  type="text"
                  id="search"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full py-3 pl-10 pr-4 transition-all duration-300 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm"
                />
              </div>
            </div>
            <div>
              <label htmlFor="role" className="block mb-2 text-sm font-medium text-gray-700">
                Role
              </label>
              <select
                id="role"
                value={roleFilter}
                onChange={handleRoleFilter}
                className="w-full px-4 py-3 transition-all duration-300 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm"
              >
                <option value="all">All Roles</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name.toLowerCase()}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="status" className="block mb-2 text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={handleStatusFilter}
                className="w-full px-4 py-3 transition-all duration-300 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label htmlFor="district" className="block mb-2 text-sm font-medium text-gray-700">
                District
              </label>
              <select
                id="district"
                value={districtFilter}
                onChange={handleDistrictFilter}
                className="w-full px-4 py-3 transition-all duration-300 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm"
              >
                <option value="all">All Districts</option>
                {geo.districts.map((d) => (
                  <option key={d.id} value={d.name}>
                    {d.name.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '')}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('all');
                  setStatusFilter('all');
                  setDistrictFilter('all');
                  setCurrentPage(1);
                }}
                className="flex items-center justify-center w-full px-4 py-3 space-x-2 font-medium text-white transition-all duration-300 shadow-md bg-gradient-to-r from-gray-500 to-slate-600 hover:from-gray-600 hover:to-slate-700 rounded-xl hover:shadow-lg"
              >
                <XMarkIcon className="w-4 h-4" />
                <span>Clear Filters</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Professional Users Table */}
      <div className="mx-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="overflow-hidden transition-all duration-300 border shadow-lg bg-white/80 backdrop-blur-sm rounded-xl border-gray-200/50 hover:shadow-xl"
        >
          <div className="p-6 border-b bg-gradient-to-r from-slate-50 to-blue-50 border-gray-200/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <UserGroupIcon className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-800">All Users </h3>
              </div>
              <div className="text-sm text-gray-600">
                Showing {users.length} of {pagination?.total || 0} users
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200/50">
              <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                <tr>
                  <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="w-4 h-4" />
                      <span>User</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                    <div className="flex items-center space-x-2">
                      <ShieldCheckIcon className="w-4 h-4" />
                      <span>Role</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="w-4 h-4" />
                      <span>Contact</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="w-4 h-4" />
                      <span>Location</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                    <div className="flex items-center space-x-2">
                      <CheckBadgeIcon className="w-4 h-4" />
                      <span>Status</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">Created</th>
                  <th className="px-6 py-4 text-xs font-semibold tracking-wider text-right text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y bg-white/50 backdrop-blur-sm divide-gray-200/50">
                <AnimatePresence>
                  {users.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: index * 0.05 }}
                      className="transition-all duration-300 hover:bg-blue-50/50"
                    >
                      <td className="px-6 py-6 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center justify-center w-10 h-10 font-semibold text-white rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
                            {capitalizeWords(user.name).charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{capitalizeWords(user.name)}</div>
                            <div className="flex items-center space-x-1 text-sm text-gray-500">
                              <EnvelopeIcon className="w-4 h-4" />
                              <span>{user.email || 'No email'}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-6 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full backdrop-blur-sm ${getRoleBadgeColor(user.role)}`}>
                          <ShieldCheckIcon className="w-3 h-3 mr-1" />
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-6 whitespace-nowrap">
                        <div className="flex items-center space-x-1 text-sm text-gray-900">
                          <PhoneIcon className="w-4 h-4 text-gray-400" />
                          <span>{user.phone || '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-6 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-1 text-sm text-gray-900">
                            <MapPinIcon className="w-4 h-4 text-gray-400" />
                            <span>{user.district?.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '') || '-'}</span>
                          </div>
                          {user.constituency && (
                            <div className="ml-5 text-xs text-gray-600">{user.constituency}</div>
                          )}
                          {(user as any).ward && (
                            <div className="ml-5 text-xs text-gray-600">Ward: {(user as any).ward}</div>
                          )}
                          {(user as any).organisation && (
                            <div className="ml-5 text-xs font-medium text-blue-600">{(user as any).organisation.name}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-6 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full backdrop-blur-sm ${getStatusBadgeColor(user.status)}`}>
                          <CheckBadgeIcon className="w-3 h-3 mr-1" />
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-6 text-sm text-gray-600 whitespace-nowrap">{formatDate(user.created_at)}</td>
                      <td className="px-6 py-6 text-sm font-medium text-right whitespace-nowrap">
                        <div className="flex justify-end space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEditModal(true);
                            }}
                            className="p-2 text-blue-600 transition-all duration-300 rounded-lg hover:text-blue-800 hover:bg-blue-50"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-2 text-red-600 transition-all duration-300 rounded-lg hover:text-red-800 hover:bg-red-50"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {users.length === 0 && !loading && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-16 text-center"
            >
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-400 to-slate-500">
                <UserGroupIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">No users found</h3>
              <p className="mb-6 text-gray-600">Try adjusting your search or filter criteria.</p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-6 py-3 mx-auto space-x-2 font-semibold text-white transition-all duration-300 shadow-lg bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-xl hover:shadow-xl"
              >
                <PlusIcon className="w-5 h-5" />
                <span>Add First User</span>
              </motion.button>
            </motion.div>
          )}

          {/* Professional Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t bg-gradient-to-r from-slate-50 to-blue-50 border-gray-200/50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-800">{(currentPage - 1) * pagination.limit + 1}</span> to{' '}
                  <span className="font-semibold text-gray-800">{Math.min(currentPage * pagination.limit, pagination.total)}</span> of{' '}
                  <span className="font-semibold text-gray-800">{formatNumberWithCommas(pagination.total)}</span> results
                </div>

                <div className="flex items-center space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-300 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </motion.button>

                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let page: number;
                      if (pagination.totalPages <= 5) {
                        page = i + 1;
                      } else {
                        const start = Math.max(1, currentPage - 2);
                        const end = Math.min(pagination.totalPages, start + 4);
                        page = start + i;
                        if (page > end) return null;
                      }

                      return (
                        <motion.button
                          key={page}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-300 ${page === currentPage
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-sm'
                            }`}
                        >
                          {page}
                        </motion.button>
                      );
                    })}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                    disabled={currentPage === pagination.totalPages}
                    className="px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-300 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {loading && users.length > 0 && (
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          roles={roles}
          organizations={organizations}
          lookups={geo}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser!}
          roles={roles}
          organizations={organizations}
          lookups={geo}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSubmit={handleUpdateUser}
        />
      )}

      {showObserversModal && (
        <ObserversModal
          observerData={observerData}
          loading={loadingObservers}
          onClose={() => setShowObserversModal(false)}
        />
      )}
    </motion.div>
  );
};

// Create User Modal Component
const CreateUserModal: React.FC<{
  roles: Role[];
  organizations: Organization[];
  lookups: Lookups;
  onClose: () => void;
  onSubmit: (data: CreateUserData) => void;
}> = ({ roles, organizations, lookups, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<CreateUserForm>({
    email: '',
    password: '',
    name: '',
    role: '' as any,
    phone: '+265',
    district: '',
    constituency: '',
    ward: '',
    organisation_id: undefined,
    status: 'active',
  });

  const [constituencies, setConstituencies] = useState<string[]>([]);
  const [wards, setWards] = useState<string[]>([]);

  // Role-based visibility helpers
  const isAdmin = formData.role === 'admin';
  const isObserver = formData.role === 'observer';

  useEffect(() => {
    // when district changes, filter constituencies by district_id
    const districtRow = lookups.districts.find((d) => d.name === formData.district);
    const district_id = districtRow?.id;

    if (district_id) {
      setConstituencies(
        lookups.constituencies.filter((c) => c.district_id === district_id).map((c) => c.name)
      );
    } else {
      setConstituencies([]);
    }
    setWards([]); // reset wards when district changes
  }, [formData.district, lookups]);

  useEffect(() => {
    // when constituency changes, filter wards by constituency_id
    const constituencyRow = lookups.constituencies.find((c) => c.name === formData.constituency);
    const constituency_id = constituencyRow?.id;

    if (constituency_id) {
      setWards(lookups.wards.filter((w) => w.constituency_id === constituency_id).map((w) => w.name));
    } else {
      setWards([]);
    }
  }, [formData.constituency, lookups]);

  const handleInputChange = (field: keyof CreateUserForm, value: any) => {
    const next: CreateUserForm = { ...formData, [field]: value };
    if (field === 'district') {
      next.constituency = '' as any;
      next.ward = '' as any;
    }
    if (field === 'constituency') {
      next.ward = '' as any;
    }
    if (field === 'name') {
      const generateEmailFromName = (fullName: string) => {
        const cleaned = fullName.trim().toLowerCase().replace(/[^a-z\s]/g, '');
        const parts = cleaned.split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '';
        const first = parts[0];
        const last = parts.length > 1 ? parts[parts.length - 1] : '';
        return last ? `${first}.${last}@masoathu.org` : `${first}@masoathu.org`;
      };
      next.email = generateEmailFromName(value);
    }
    if (field === 'role') {
      if (value === 'admin') {
        next.district = '' as any;
        next.constituency = '' as any;
        next.ward = '' as any;
      } else if (value === 'coordinator') {
        next.constituency = '' as any;
        next.ward = '' as any;
      }
    }
    setFormData(next);
  };

  const handlePhoneChange = (raw: string) => {
    // Allow only + and digits, enforce +265 prefix
    let val = raw.replace(/[^+\d]/g, '');
    if (!val.startsWith('+265')) {
      val = '+265' + val.replace(/^\+?265?/, '');
    }
    if (val.length < 4) val = '+265';
    // Derive password from phone minus +265 prefix
    const pwd = val.replace(/^\+265/, '');
    setFormData((prev) => ({ ...prev, phone: val, password: pwd }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Cast to CreateUserData – ward is optional and may be ignored server-side
    onSubmit(formData as CreateUserData);
  };

  return (
    <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative w-full max-w-2xl p-5 mx-auto bg-white border rounded-md shadow-lg top-10">
        <button
          type="button"
          onClick={onClose}
          className="absolute text-gray-400 transition-colors duration-150 rounded hover:text-gray-600 focus:outline-none top-3 right-3"
          aria-label="Close"
          title="Close"
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        <div className="mt-3">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Create New User</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name and Phone side-by-side */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="name" className="block mb-1 text-sm font-medium text-gray-700">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block mb-1 text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+265999123456"
                />
              </div>
            </div>

            {/* Password next (auto-filled from phone minus +265) */}
            <div>
              <label htmlFor="password" className="block mb-1 text-sm font-medium text-gray-700">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                required
                minLength={6}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Email last (auto-generated from name) */}
            <div>
              <label htmlFor="email" className="block mb-1 text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Role and Organization */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="role" className="block mb-1 text-sm font-medium text-gray-700">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="role"
                  required
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Role</option>
                  <option value="admin">Administrator role</option>
                  <option value="observer">Observer role</option>
                  <option value="coordinator">Coordinator role</option>
                </select>
              </div>
              <div>
                <label htmlFor="organisation" className="block mb-1 text-sm font-medium text-gray-700">
                  Organization
                </label>
                <select
                  id="organisation"
                  value={formData.organisation_id || ''}
                  onChange={(e) => handleInputChange('organisation_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Organization</option>
                  {Array.isArray(organizations) &&
                    organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {!isAdmin && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="district" className="block mb-1 text-sm font-medium text-gray-700">
                    District
                  </label>
                  <select
                    id="district"
                    value={formData.district}
                    onChange={(e) => handleInputChange('district', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select District</option>
                    {lookups.districts.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '')}
                      </option>
                    ))}
                  </select>
                </div>
                {isObserver && (
                  <div>
                    <label htmlFor="constituency" className="block mb-1 text-sm font-medium text-gray-700">
                      Constituency
                    </label>
                    <select
                      id="constituency"
                      value={formData.constituency}
                      onChange={(e) => handleInputChange('constituency', e.target.value)}
                      disabled={!formData.district}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!formData.district ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                    >
                      <option value="">Select Constituency</option>
                      {constituencies.map((constituency) => (
                        <option key={constituency} value={constituency}>
                          {constituency}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Ward (only for observers) */}
            {isObserver && (
              <div>
                <label htmlFor="ward" className="block mb-1 text-sm font-medium text-gray-700">
                  Ward
                </label>
                <select
                  id="ward"
                  value={formData.ward || ''}
                  onChange={(e) => handleInputChange('ward', e.target.value)}
                  disabled={!formData.constituency}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!formData.constituency ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                >
                  <option value="">Select Ward</option>
                  {wards.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label htmlFor="status" className="block mb-1 text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex justify-end pt-4 space-x-3">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Create User
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit User Modal Component
const EditUserModal: React.FC<{
  user: User;
  roles: Role[];
  organizations: Organization[];
  lookups: Lookups;
  onClose: () => void;
  onSubmit: (data: UpdateUserData) => void;
}> = ({ user, roles, organizations, lookups, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<UpdateUserForm>({
    name: user.name,
    role: user.role as "admin" | "coordinator" | "observer",
    phone: user.phone || '',
    district: user.district || '',
    constituency: user.constituency || '',
    ward: (user as any).ward || '',
    organisation_id: user.organisation_id || undefined,
    status: user.status as "active" | "inactive",
  });

  const [constituencies, setConstituencies] = useState<string[]>([]);
  const [wards, setWards] = useState<string[]>([]);

  // Role-based visibility helpers
  const isAdmin = formData.role === 'admin';
  const isObserver = formData.role === 'observer';

  useEffect(() => {
    // Normalize phone to include +265 prefix
    if (formData.phone && !formData.phone.startsWith('+265')) {
      setFormData((prev) => ({ ...prev, phone: '+265' + (formData.phone ?? '').replace(/^\+?265?/, '') }));
    }
  }, []);

  useEffect(() => {
    const dRow = lookups.districts.find((d) => d.name === formData.district);
    const dId = dRow?.id;
    const cs = dId
      ? lookups.constituencies.filter((c) => c.district_id === dId).map((c) => c.name)
      : [];
    setConstituencies(cs);
    // If current constituency no longer matches new district → clear it
    if (formData.constituency && !lookups.constituencies.some((c) => c.name === formData.constituency && c.district_id === dId)) {
      setFormData((prev) => ({ ...prev, constituency: '', ward: '' }));
      setWards([]);
    }
  }, [formData.district, lookups]);

  useEffect(() => {
    const cRow = lookups.constituencies.find((c) => c.name === formData.constituency);
    const cId = cRow?.id;
    const ws = cId ? lookups.wards.filter((w) => w.constituency_id === cId).map((w) => w.name) : [];
    setWards(ws);
    if (formData.ward && !lookups.wards.some((w) => w.name === formData.ward && w.constituency_id === cId)) {
      setFormData((prev) => ({ ...prev, ward: '' }));
    }
  }, [formData.constituency, lookups]);

  const handleInputChange = (field: keyof UpdateUserForm, value: any) => {
    const next: UpdateUserForm = { ...formData, [field]: value };
    if (field === 'district') {
      next.constituency = '' as any;
      next.ward = '' as any;
    }
    if (field === 'constituency') {
      next.ward = '' as any;
    }
    if (field === 'role') {
      if (value === 'admin') {
        next.district = '' as any;
        next.constituency = '' as any;
        next.ward = '' as any;
      } else if (value === 'coordinator') {
        next.constituency = '' as any;
        next.ward = '' as any;
      }
    }
    setFormData(next);
  };

  const handlePhoneChange = (raw: string) => {
    let val = raw.replace(/[^+\d]/g, '');
    if (!val.startsWith('+265')) {
      val = '+265' + val.replace(/^\+?265?/, '');
    }
    if (val.length < 4) val = '+265';
    setFormData((prev) => ({ ...prev, phone: val }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData as UpdateUserData);
  };

  return (
    <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative max-w-2xl p-5 mx-auto bg-white border rounded-md shadow-lg top-10">
        <button
          type="button"
          onClick={onClose}
          className="absolute text-gray-400 transition-colors duration-150 rounded hover:text-gray-600 focus:outline-none top-3 right-3"
          aria-label="Close"
          title="Close"
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        <div className="mt-3">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Edit User</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name and Phone side-by-side */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="name" className="block mb-1 text-sm font-medium text-gray-700">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block mb-1 text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+265999123456"
                />
              </div>
            </div>

            {/* Role and Organization */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="role" className="block mb-1 text-sm font-medium text-gray-700">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="role"
                  required
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="admin">Administrator role</option>
                  <option value="observer">Observer role</option>
                  <option value="coordinator">Coordinator role</option>
                </select>
              </div>
              <div>
                <label htmlFor="organisation" className="block mb-1 text-sm font-medium text-gray-700">
                  Organization
                </label>
                <select
                  id="organisation"
                  value={formData.organisation_id || ''}
                  onChange={(e) => handleInputChange('organisation_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Organization</option>
                  {Array.isArray(organizations) &&
                    organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {!isAdmin && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="district" className="block mb-1 text-sm font-medium text-gray-700">
                    District
                  </label>
                  <select
                    id="district"
                    value={formData.district}
                    onChange={(e) => handleInputChange('district', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select District</option>
                    {lookups.districts.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '')}
                      </option>
                    ))}
                  </select>
                </div>
                {isObserver && (
                  <div>
                    <label htmlFor="constituency" className="block mb-1 text-sm font-medium text-gray-700">
                      Constituency
                    </label>
                    <select
                      id="constituency"
                      value={formData.constituency}
                      onChange={(e) => handleInputChange('constituency', e.target.value)}
                      disabled={!formData.district}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!formData.district ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                    >
                      <option value="">Select Constituency</option>
                      {constituencies.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {isObserver && (
              <div>
                <label htmlFor="ward" className="block mb-1 text-sm font-medium text-gray-700">
                  Ward
                </label>
                <select
                  id="ward"
                  value={formData.ward || ''}
                  onChange={(e) => handleInputChange('ward', e.target.value)}
                  disabled={!formData.constituency}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${!formData.constituency ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                >
                  <option value="">Select Ward</option>
                  {wards.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label htmlFor="status" className="block mb-1 text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex justify-end pt-4 space-x-3">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Update User
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Observers Modal Component to display all observers and ward allocation status
const ObserversModal: React.FC<{
  observerData: {
    observers: Array<{
      id: number;
      name: string;
      phone: string;
      district: string;
      constituency?: string;
      ward?: string;
    }>;
    allocatedWards: string[];
    unallocatedWards: string[];
  };
  loading: boolean;
  onClose: () => void;
}> = ({ observerData, loading, onClose }) => {
  const [activeTab, setActiveTab] = useState<'observers' | 'unallocated'>('observers');
  const [searchTerm, setSearchTerm] = useState('');
  const [districtFilter, setDistrictFilter] = useState<string>('');

  // Get unique districts for filtering using Array.from instead of spread operator to fix TS error
  const uniqueDistricts = Array.from(
    new Set(observerData.observers.map(obs => obs.district))
  ).filter(Boolean).sort();

  // Filter observers based on search and district filter
  const filteredObservers = observerData.observers
    .filter(obs =>
    (searchTerm === '' ||
      obs.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      obs.phone.includes(searchTerm) ||
      (obs.ward && obs.ward.toLowerCase().includes(searchTerm.toLowerCase())))
    )
    .filter(obs => districtFilter === '' || obs.district === districtFilter);

  // Filter unallocated wards based on search
  const filteredUnallocatedWards = observerData.unallocatedWards
    .filter(ward => searchTerm === '' || ward.toLowerCase().includes(searchTerm.toLowerCase()));

  // Calculate stats
  const totalWards = observerData.allocatedWards.length + observerData.unallocatedWards.length;
  const allocatedPercentage = totalWards > 0
    ? Math.round((observerData.allocatedWards.length / totalWards) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative max-w-6xl p-0 mx-auto bg-white border shadow-xl rounded-xl top-10">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-900 via-blue-800 to-slate-900 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/10 backdrop-blur-sm">
                <UserGroupIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white">Observer Ward Allocations</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 text-white transition-colors duration-150 rounded-full bg-white/10 hover:bg-white/20"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
              <div className="text-xs text-blue-200">Total Observers</div>
              <div className="text-xl font-bold text-white">{observerData.observers.length}</div>
            </div>
            <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
              <div className="text-xs text-blue-200">Total Wards</div>
              <div className="text-xl font-bold text-white">{totalWards}</div>
            </div>
            <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
              <div className="text-xs text-blue-200">Allocated Wards</div>
              <div className="text-xl font-bold text-white">
                {observerData.allocatedWards.length}
                <span className="ml-1 text-sm font-normal text-green-300">({allocatedPercentage}%)</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
              <div className="text-xs text-blue-200">Unallocated Wards</div>
              <div className="text-xl font-bold text-white">
                {observerData.unallocatedWards.length}
                <span className="ml-1 text-sm font-normal text-amber-300">
                  ({100 - allocatedPercentage}%)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex space-x-4">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeTab === 'observers'
                ? 'bg-blue-100 text-blue-800'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
              onClick={() => setActiveTab('observers')}
            >
              All Observers ({observerData.observers.length})
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${activeTab === 'unallocated'
                ? 'bg-amber-100 text-amber-800'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
              onClick={() => setActiveTab('unallocated')}
            >
              Unallocated Wards ({observerData.unallocatedWards.length})
            </button>
          </div>

          <div className="flex items-center space-x-3">
            {activeTab === 'observers' && (
              <select
                value={districtFilter}
                onChange={(e) => setDistrictFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Districts</option>
                {uniqueDistricts.map(district => (
                  <option key={district} value={district}>
                    {district.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '')}
                  </option>
                ))}
              </select>
            )}

            <div className="relative">
              <MagnifyingGlassIcon className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                placeholder={activeTab === 'observers' ? "Search observers..." : "Search wards..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 py-2 pr-4 text-sm border border-gray-300 rounded-lg pl-9 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto border-4 border-blue-200 rounded-full border-t-blue-500 animate-spin"></div>
                <p className="mt-3 text-sm text-gray-600">Loading data...</p>
              </div>
            </div>
          ) : activeTab === 'observers' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">Observer</th>
                    <th className="px-6 py-3 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">Phone</th>
                    <th className="px-6 py-3 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">District</th>
                    <th className="px-6 py-3 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">Constituency</th>
                    <th className="px-6 py-3 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">Ward</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredObservers.length > 0 ? (
                    filteredObservers.map(observer => (
                      <tr key={observer.id} className="transition-colors hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{observer.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{observer.phone}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {observer.district.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{observer.constituency || '-'}</td>
                        <td className="px-6 py-4">
                          {observer.ward ? (
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                              {observer.ward}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                              No ward assigned
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-sm text-center text-gray-500">
                        No observers match your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-red-600">Unallocated Wards ({filteredUnallocatedWards.length})</h4>
                <p className="text-sm text-gray-600">These wards do not have observers assigned to them</p>
              </div>

              {filteredUnallocatedWards.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {filteredUnallocatedWards.map((ward, index) => (
                    <div key={index} className="px-4 py-3 border rounded-lg border-amber-200 bg-amber-50">
                      <div className="text-sm font-medium text-amber-800">{ward}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center p-12 rounded-lg bg-gray-50">
                  {searchTerm ? (
                    <p className="text-gray-500">No unallocated wards match your search criteria</p>
                  ) : (
                    <div className="text-center">
                      <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-green-500 bg-green-100 rounded-full">
                        <CheckBadgeIcon className="w-8 h-8" />
                      </div>
                      <p className="text-lg font-medium text-green-600">All wards have been allocated!</p>
                      <p className="text-sm text-gray-500">Every ward has an observer assigned to it</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 text-right border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Users;
